import groupBy from 'lodash/groupBy';
import range from 'lodash/range';
import upperFirst from 'lodash/upperFirst';

import { formatDateShort } from '~/services/TimeService';

import { pluralizeToString } from '~/utils/stringUtils';

import { nameKey } from '../tabs/egs/previousEgs';
import { round2 } from '../tabs/forecasting/forecastingModel';
import type { OverrideBasis } from '../tabs/overrides/types';
import {
  SEASON_GRIDS,
  WEEKDAYS,
  WEEKDAY_LABELS,
  emptyWeekdayAmounts,
  emptyWeekdayCosts,
} from '../tabs/surcharges/surchargeGridsModel';
import type { SeasonLabel } from '../tabs/surcharges/types';

import {
  andList,
  cellAddress,
  cellAt,
  columnLetter,
  excelErrorOf,
  findLabel,
  isBlank,
  isUnpriced,
  isoDateOf,
  monthKeyOf,
  numberOf,
  periodDates,
  positiveNumberOf,
  priceOf,
  priceProblem,
  textOf,
  unusablePriceProblem,
} from './sheetCells';
import type {
  ImportIssue,
  ImportSection,
  ParsedBlackout,
  ParsedEgsPrice,
  ParsedEgsRoom,
  ParsedForecastRoom,
  ParsedOverrideTier,
  ParsedOverrides,
  ParsedPropertySetup,
  ParsedSurchargeGrid,
  ParsedSurchargePeriod,
  SheetContext,
  SheetRows,
} from './types';

const SECTION_LABEL_COL = 1;
const FIRST_DATA_COL = 2;
// Keeps a sub-label search from running into the next section.
const SUB_LABEL_REACH = 12;
const HEADER_ROWS = 10;
const HEADER_VALUE_REACH = 3;
// The pricing table repeats "Overrides" further right as a column heading.
const OVERRIDES_LAST_COL = 5;
const OVERRIDE_BASES: Record<string, OverrideBasis> = {
  'hotel revenue (local)': 'vendor_revenue',
  'room nights': 'room_nights',
};
const OVERRIDES_OFF = 'none';

function report(
  context: SheetContext,
  section: ImportSection,
  severity: ImportIssue['severity'],
  row: number,
  col: number,
  message: string,
) {
  context.issues.push({ section, severity, cell: cellAddress(row, col), message });
}

function missingSection(context: SheetContext, section: ImportSection, problem: string) {
  context.issues.push({
    section,
    severity: 'error',
    message: `${problem} on the "${context.sheet}" sheet, so no ${section} will load.`,
  });
}

function findSubLabel(rows: SheetRows, label: string, sectionRow: number) {
  return findLabel(rows, label, { fromRow: sectionRow, toRow: sectionRow + SUB_LABEL_REACH, col: SECTION_LABEL_COL });
}

interface DatedSection {
  section: Extract<ImportSection, 'blackouts' | 'surcharges'>;
  heading: string;
  noun: string;
  // A column holding only amounts is still an entry, reported for its missing dates.
  isUsed?: (col: number) => boolean;
  // Checked before numbering, so a rejected entry doesn't use up an unnamed entry's number.
  rejection?: (col: number) => { row: number; problem: string } | null;
}

const DATED_TABS: Record<DatedSection['section'], string> = { blackouts: 'Blackouts', surcharges: 'Surcharges' };

interface DatedColumn extends ParsedBlackout {
  col: number;
}

// Found by content, not a fixed stride: merged cells put entries two apart and a PCM can leave gaps.
function readDatedColumns(
  context: SheetContext,
  sectionRow: number,
  { section, heading, noun, isUsed = () => false, rejection = () => null }: DatedSection,
): Array<DatedColumn> {
  const { rows } = context;
  const nameRow = findSubLabel(rows, 'Period Name', sectionRow);
  const fromRow = findSubLabel(rows, 'Date From', sectionRow);
  const toRow = findSubLabel(rows, 'Date To', sectionRow);
  if (!nameRow || !fromRow || !toRow) {
    missingSection(context, section, `The ${heading} section has no Period Name, Date From and Date To rows`);
    return [];
  }

  // The whole section, not just the date rows: an amount typed without dates can sit past the last date.
  const width = Math.max(0, ...rows.slice(sectionRow, sectionRow + SUB_LABEL_REACH).map((cells) => cells.length));
  const entries: Array<DatedColumn> = [];
  const unnamed: Array<{ col: number; dates: string }> = [];
  for (let col = FIRST_DATA_COL; col < width; col++) {
    const name = textOf(cellAt(rows, nameRow.row, col));
    const fromCell = cellAt(rows, fromRow.row, col);
    const toCell = cellAt(rows, toRow.row, col);
    if (name == null && fromCell == null && toCell == null && !isUsed(col)) {
      continue;
    }

    const startDate = isoDateOf(fromCell);
    const endDate = isoDateOf(toCell);
    const entry = name ? `The "${name}" ${noun}` : `The ${noun} in column ${cellAddress(0, col).replace(/\d+/g, '')}`;
    // The rest of the section still loads, so the entry is lighter to add on its tab than to upload again.
    const fixIt = `Add it on ${DATED_TABS[section]}.`;
    if (!startDate || !endDate) {
      report(
        context,
        section,
        'error',
        fromRow.row,
        col,
        `${entry} won't load: it needs both a Date From and a Date To. ${fixIt}`,
      );
      continue;
    }
    const dates = periodDates({ startDate, endDate });
    if (startDate > endDate) {
      report(
        context,
        section,
        'error',
        fromRow.row,
        col,
        `${entry} won't load: it ends before it starts (${dates}). ${fixIt}`,
      );
      continue;
    }
    const rejected = rejection(col);
    if (rejected) {
      report(context, section, 'error', rejected.row, col, `${entry} won't load: ${rejected.problem} ${fixIt}`);
      continue;
    }
    if (name == null) {
      unnamed.push({ col, dates });
    }
    entries.push({ col, name, startDate, endDate });
  }
  // One note for all of them: the numbers run in column order, matching the names they load with.
  if (unnamed.length > 0) {
    const label = upperFirst(noun);
    const message =
      unnamed.length === 1
        ? `The ${noun} from ${unnamed[0].dates} has no Period Name, so it loads as "${label} 1".`
        : `${pluralizeToString(noun, unnamed.length)} have no Period Name, so they load as "${label} 1" to "${label} ${unnamed.length}".`;
    report(context, section, 'warning', nameRow.row, unnamed[0].col, message);
  }
  return entries;
}

export function parseBlackouts(context: SheetContext): Array<ParsedBlackout> {
  const heading = findLabel(context.rows, 'Blackouts', { col: SECTION_LABEL_COL });
  if (!heading) {
    missingSection(context, 'blackouts', 'There is no Blackouts section');
    return [];
  }
  return readDatedColumns(context, heading.row, { section: 'blackouts', heading: 'Blackouts', noun: 'blackout' }).map(
    ({ name, startDate, endDate }) => ({ name, startDate, endDate }),
  );
}

const PERIODS_HEADING = 'Special Events & Periods';

// Sell AUD and Cost Local are what the PCM types; Sell Local and Cost AUD are formulas over them.
export function parseSurchargePeriods(context: SheetContext): Array<ParsedSurchargePeriod> {
  const { rows } = context;
  const heading = findLabel(rows, PERIODS_HEADING, { col: SECTION_LABEL_COL });
  if (!heading) {
    missingSection(context, 'surcharges', `There is no ${PERIODS_HEADING} section`);
    return [];
  }
  const sellRow = findSubLabel(rows, 'Surcharge Sell AUD', heading.row);
  const costRow = findSubLabel(rows, 'Surcharge Cost Local', heading.row);
  if (!sellRow || !costRow) {
    missingSection(
      context,
      'surcharges',
      `The ${PERIODS_HEADING} section has no Surcharge Sell AUD and Surcharge Cost Local rows`,
    );
    return [];
  }
  const sellAt = (col: number) => priceOf(cellAt(rows, sellRow.row, col));
  const costAt = (col: number) => numberOf(cellAt(rows, costRow.row, col));

  return readDatedColumns(context, heading.row, {
    section: 'surcharges',
    heading: PERIODS_HEADING,
    noun: 'period',
    // Unused columns hold formula zeros, so only a non-zero amount marks one as a period.
    isUsed: (col) => !!sellAt(col) || !!costAt(col),
    rejection: (col) => {
      if (sellAt(col) === null) {
        const sell = cellAt(rows, sellRow.row, col);
        const problem = isUnpriced(sell)
          ? "it has no Surcharge Sell AUD, and Surcharges won't save a period without one."
          : unusablePriceProblem(sell, 'Surcharge Sell AUD');
        return { row: sellRow.row, problem };
      }
      // A blank cost is the tab's to estimate; one that can't be read would pass for that estimate.
      const cost = cellAt(rows, costRow.row, col);
      const costNumber = costAt(col);
      if (!isBlank(cost) && (costNumber === null || costNumber < 0)) {
        return { row: costRow.row, problem: unusablePriceProblem(cost, 'Surcharge Cost Local') };
      }
      return null;
    },
  }).map(({ col, ...period }) => {
    const cost = costAt(col);
    return { ...period, sellAud: sellAt(col), cost: cost === null ? null : round2(cost) };
  });
}

const GRID_HEADING = 'Surcharges by DOW';
const SEASONALITY_HEADING = 'Assign Seasonality to Travel Months';
const SEASON_BY_TEXT: Record<string, SeasonLabel> = { high: 'high', shoulder: 'shoulder' };
// Not an error: periods still load when the grid can't be read.
function gridNotRead(context: SheetContext, problem: string, outcome: string) {
  context.issues.push({ section: 'surcharges', severity: 'warning', message: `${problem}, so ${outcome}.` });
}

function parseSeasonMonths(context: SheetContext): Record<string, SeasonLabel> {
  const { rows } = context;
  const heading = findLabel(rows, SEASONALITY_HEADING, { col: SECTION_LABEL_COL });
  const monthRow = heading && findSubLabel(rows, 'Month', heading.row);
  const seasonRow = heading && findSubLabel(rows, 'High/Shoulder Season?', heading.row);
  if (!monthRow || !seasonRow) {
    gridNotRead(
      context,
      `The "${context.sheet}" sheet has no ${SEASONALITY_HEADING} section with Month and High/Shoulder Season? rows`,
      'no month is marked High or Shoulder',
    );
    return {};
  }

  const seasonByMonth: Record<string, SeasonLabel> = {};
  const cells = rows[seasonRow.row] ?? [];
  for (let col = FIRST_DATA_COL; col < cells.length; col++) {
    const text = textOf(cells[col]);
    if (!text) {
      continue;
    }
    const season = SEASON_BY_TEXT[text.toLowerCase()];
    const month = monthKeyOf(cellAt(rows, monthRow.row, col));
    if (!season || !month) {
      report(
        context,
        'surcharges',
        'warning',
        seasonRow.row,
        col,
        `"${text}" in High/Shoulder Season? isn't High or Shoulder under a month, so it doesn't load. Mark the month on Surcharges.`,
      );
      continue;
    }
    seasonByMonth[month] = season;
  }
  return seasonByMonth;
}

// Sell AUD and Cost Local are what the PCM types, as in the periods below the grid.
export function parseSurchargeGrid(context: SheetContext): ParsedSurchargeGrid | null {
  const { rows } = context;
  const heading = findLabel(rows, GRID_HEADING, { col: SECTION_LABEL_COL });
  const seasonRow = heading && findSubLabel(rows, 'Season', heading.row);
  const dowRow = heading && findSubLabel(rows, 'DOW', heading.row);
  const sellRow = heading && findSubLabel(rows, 'Surcharge Sell AUD', heading.row);
  const costRow = heading && findSubLabel(rows, 'Surcharge Cost Local', heading.row);
  // Only read to explain a missing Sell AUD: the AUD row is the one the workbook prices with.
  const sellLocalRow = heading && findSubLabel(rows, 'Surcharge Sell Local', heading.row);
  const noGrid = "the weekday grid won't load";
  if (!seasonRow || !dowRow || !sellRow || !costRow) {
    gridNotRead(
      context,
      `The "${context.sheet}" sheet has no ${GRID_HEADING} section with Season, DOW, Surcharge Sell AUD and Surcharge Cost Local rows`,
      noGrid,
    );
    return null;
  }

  const sellAud = { allYearRound: emptyWeekdayAmounts(), high: emptyWeekdayAmounts(), shoulder: emptyWeekdayAmounts() };
  const costLocal = { allYearRound: emptyWeekdayCosts(), high: emptyWeekdayCosts(), shoulder: emptyWeekdayCosts() };
  for (const { key, label } of SEASON_GRIDS) {
    const block = findLabel(rows, label, { fromRow: seasonRow.row, toRow: seasonRow.row });
    // Days come from the DOW row, so a reordered block reads nothing rather than the wrong days.
    const days =
      block &&
      WEEKDAYS.every(
        (day, offset) =>
          textOf(cellAt(rows, dowRow.row, block.col + offset))?.toLowerCase() === WEEKDAY_LABELS[day].toLowerCase(),
      );
    if (!block || !days) {
      gridNotRead(context, `The ${label} season in ${GRID_HEADING} doesn't have Mon to Sun under it`, noGrid);
      return null;
    }

    const costWithoutSell: Array<string> = [];
    const localSellOnly: Array<string> = [];
    WEEKDAYS.forEach((day, offset) => {
      const col = block.col + offset;
      const where = `${label} ${WEEKDAY_LABELS[day]}`;
      const sellCell = cellAt(rows, sellRow.row, col);
      const sell = priceOf(sellCell);
      if (sell === null && !isUnpriced(sellCell)) {
        report(
          context,
          'surcharges',
          'warning',
          sellRow.row,
          col,
          `On ${where}, ${priceProblem(sellCell, 'Surcharge Sell AUD')}, so it loads as no surcharge. Add it on Surcharges.`,
        );
      }
      const costCell = cellAt(rows, costRow.row, col);
      const cost = numberOf(costCell);
      const usableCost = cost !== null && cost >= 0 ? cost : null;
      if (usableCost === null && !isBlank(costCell)) {
        report(
          context,
          'surcharges',
          'warning',
          costRow.row,
          col,
          `On ${where}, ${priceProblem(costCell, 'Surcharge Cost Local')}, so it loads blank. Add it on Surcharges.`,
        );
      }
      sellAud[key][day] = sell ?? 0;
      // The template's Cost Local is a formula showing 0 wherever no sell was typed.
      costLocal[key][day] = usableCost !== null && (sell !== null || usableCost > 0) ? round2(usableCost) : null;
      // Typed over the Sell Local formula while the workbook takes the sell in AUD, so Excel prices it at 0 too.
      if (sell === null && sellLocalRow && priceOf(cellAt(rows, sellLocalRow.row, col)) !== null) {
        localSellOnly.push(WEEKDAY_LABELS[day]);
      } else if (sell === null && usableCost !== null && usableCost > 0) {
        costWithoutSell.push(WEEKDAY_LABELS[day]);
      }
    });
    if (localSellOnly.length > 0) {
      report(
        context,
        'surcharges',
        'warning',
        sellRow.row,
        block.col,
        `${label} has a Sell Local but no Sell AUD on ${andList(localSellOnly)}. The workbook takes the sell in AUD, so the local one isn't used. Add the sell on Surcharges.`,
      );
    }
    if (costWithoutSell.length > 0) {
      report(
        context,
        'surcharges',
        'warning',
        sellRow.row,
        block.col,
        `${label} has a cost but no Sell AUD on ${andList(costWithoutSell)}. Add the sell on Surcharges.`,
      );
    }
  }
  return { sellAud, costLocal, seasonByMonth: parseSeasonMonths(context) };
}

const EGS_GUESTS = [
  { key: 'adult', label: 'Adult' },
  { key: 'child', label: 'Child' },
] as const;
const EGS_HEADER = 'Extra Guest Surcharge';
const EGS_COST_TABLE = 'Net Rate Local';
const EGS_SELL_TABLE = 'Sell Price AUD';
// The label in column B under each table's last room slot. Sell Price AUD shares its rows with Net Rate AUD.
const EGS_COST_END = 'Cost per Night Local';
const EGS_SELL_END = 'Pro Rata Calculator';

type EgsGuestKey = (typeof EGS_GUESTS)[number]['key'];

interface EgsTable {
  title: string;
  headerRow: number;
  adultCol: number;
}

// Tables sit side by side, so Adult and Child must come after the title and before the next table's.
function egsTable(rows: SheetRows, title: string): EgsTable | null {
  const found = findLabel(rows, title);
  if (!found) {
    return null;
  }
  const titleCells = rows[found.row] ?? [];
  const nextTitleCol = titleCells.findIndex(
    (cell, col) => col > found.col && textOf(cell) !== null && textOf(cell) !== EGS_HEADER,
  );
  const headerRow = found.row + 1;
  const adult = findLabel(rows, 'Adult', {
    fromRow: headerRow,
    toRow: headerRow,
    firstCol: found.col + 1,
    lastCol: nextTitleCol === -1 ? Infinity : nextTitleCol - 1,
  });
  if (!adult || !findLabel(rows, 'Child', { fromRow: headerRow, toRow: headerRow, col: adult.col + 1 })) {
    return null;
  }
  return { title, headerRow, adultCol: adult.col };
}

// A room slot can be blank, so the rooms run to the end label rather than to the first gap.
function egsTableRooms(context: SheetContext, { title, headerRow }: EgsTable, end: string) {
  const { rows } = context;
  const endRow = findLabel(rows, end, { fromRow: headerRow + 1, col: SECTION_LABEL_COL })?.row;
  if (endRow === undefined) {
    context.issues.push({
      section: 'extraGuestSurcharges',
      severity: 'error',
      message: `The ${title} table on the "${context.sheet}" sheet has no "${end}" row under it, so no extra guest surcharges will load.`,
    });
    return null;
  }
  return range(headerRow + 1, endRow).flatMap((row) => {
    const room = textOf(cellAt(rows, row, SECTION_LABEL_COL));
    return room ? [{ row, room }] : [];
  });
}

interface EgsPartial {
  guest: string;
  room: string;
  missing: 'cost' | 'sell';
}

// The Extra Guest Surcharges tab keeps cents, unlike the whole dollars priceOf rounds to.
function egsAmountOf(value: unknown): number | null {
  const number = positiveNumberOf(value);
  return number === null ? null : round2(number);
}

// Both are needed: a missing sell loads as 0, which the tab accepts, and a missing cost reads as free. A 0 counts as
// missing, since the template's formula cells show 0 wherever nothing was typed.
function egsPartialNotes(context: SheetContext, partials: Array<EgsPartial>) {
  const byGuest = groupBy(partials, ({ missing, guest }) => `${missing}::${guest}`);
  const byRooms = groupBy(Object.values(byGuest), (group) => `${group[0].missing}::${group.map(({ room }) => room)}`);
  Object.values(byRooms).forEach((groups) => {
    const [{ missing }] = groups[0];
    const guests = groups.map((group) => group[0].guest);
    const rooms = groups[0].map(({ room }) => `"${room}"`);
    const one = guests.length === 1 && rooms.length === 1;
    const has =
      missing === 'sell'
        ? `a cost but no ${EGS_SELL_TABLE} (blank or 0)`
        : `a ${EGS_SELL_TABLE} but no cost (blank or 0)`;
    context.issues.push({
      section: 'extraGuestSurcharges',
      severity: 'warning',
      message: `The extra ${andList(guests)} ${guests.length === 1 ? 'surcharge' : 'surcharges'} for ${andList(rooms)} won't load: ${one ? 'it has' : 'each has'} ${has}. Add ${one ? 'it' : 'them'} on the Extra Guest Surcharges tab.`,
    });
  });
}

// Cost Local and Sell AUD, whichever way the PCM typed them: the other table is a formula over each.
export function parseExtraGuestSurcharges(context: SheetContext): Array<ParsedEgsRoom> {
  const { rows } = context;
  const cost = egsTable(rows, EGS_COST_TABLE);
  const sell = egsTable(rows, EGS_SELL_TABLE);
  if (!cost || !sell) {
    context.issues.push({
      section: 'extraGuestSurcharges',
      severity: 'error',
      message: `The "${context.sheet}" sheet has no ${EGS_COST_TABLE} and ${EGS_SELL_TABLE} tables with ${EGS_HEADER} Adult and Child columns, so no extra guest surcharges will load.`,
    });
    return [];
  }
  const costRooms = egsTableRooms(context, cost, EGS_COST_END);
  const sellRooms = egsTableRooms(context, sell, EGS_SELL_END);
  if (!costRooms || !sellRooms) {
    return [];
  }

  const sellRowByRoom = new Map(sellRooms.map(({ row, room }) => [room, row]));
  const partials: Array<EgsPartial> = [];
  const parsed: Array<ParsedEgsRoom> = [];
  for (const { row, room } of costRooms) {
    const sellRow = sellRowByRoom.get(room);
    const prices: Record<EgsGuestKey, ParsedEgsPrice | null> = { adult: null, child: null };
    EGS_GUESTS.forEach(({ key, label }, offset) => {
      const cells = [
        { table: cost, row, value: cellAt(rows, row, cost.adultCol + offset) },
        {
          table: sell,
          row: sellRow,
          value: sellRow === undefined ? null : cellAt(rows, sellRow, sell.adultCol + offset),
        },
      ];
      const amounts = cells.map(({ value }) => egsAmountOf(value));
      const unusable = cells.find(({ value }, index) => amounts[index] === null && !isUnpriced(value));
      if (unusable) {
        report(
          context,
          'extraGuestSurcharges',
          'error',
          unusable.row,
          unusable.table.adultCol + offset,
          `The extra ${label.toLowerCase()} surcharge for "${room}" won't load: ${unusablePriceProblem(unusable.value, `${unusable.table.title} ${label}`)} Add it on the Extra Guest Surcharges tab.`,
        );
        return;
      }
      const [costPrice, sellPrice] = amounts;
      if (costPrice !== null && sellPrice !== null) {
        prices[key] = { cost: costPrice, sell: sellPrice };
      } else if (costPrice !== null || sellPrice !== null) {
        partials.push({ guest: label.toLowerCase(), room, missing: costPrice === null ? 'cost' : 'sell' });
      }
    });
    if (prices.adult || prices.child) {
      parsed.push({ room, ...prices });
    }
  }
  egsPartialNotes(context, partials);
  return parsed;
}

const FORECAST_TABLE = 'Forecasted Packages';
const FORECAST_TOTAL = 'Total';

function forecastError(context: SheetContext, problem: string) {
  context.issues.push({
    section: 'forecastedPackages',
    severity: 'error',
    message: `${problem} on the "${context.sheet}" sheet, so no forecasted packages will load.`,
  });
}

function wholeNightsOf(value: unknown): number | null {
  const number = numberOf(value);
  return number !== null && number > 0 && Number.isInteger(number) ? number : null;
}

function countProblem(value: unknown): string {
  const error = excelErrorOf(value);
  if (error) {
    return `it shows ${error}`;
  }
  const shown = textOf(value) ?? (typeof value === 'number' ? String(value) : null);
  return shown ? `"${shown}" isn't a number of packages` : "it isn't a number of packages";
}

// Columns line up with the Sell Price AUD table's; rooms run to the Total row, past blank slots.
export function parseForecastedPackages(context: SheetContext): Array<ParsedForecastRoom> {
  const { rows } = context;
  const title = findLabel(rows, FORECAST_TABLE);
  if (!title) {
    forecastError(context, `There's no ${FORECAST_TABLE} table`);
    return [];
  }
  const headerRow = title.row + 1;
  const totalCol = findLabel(rows, FORECAST_TOTAL, { fromRow: headerRow, toRow: headerRow, firstCol: title.col })?.col;
  const endRow =
    totalCol === undefined
      ? undefined
      : findLabel(rows, FORECAST_TOTAL, { fromRow: headerRow + 1, col: totalCol - 1 })?.row;
  if (totalCol === undefined || endRow === undefined) {
    forecastError(context, `The ${FORECAST_TABLE} table has no Total column and row`);
    return [];
  }
  const sellTitle = findLabel(rows, EGS_SELL_TABLE);
  const sellEndRow = sellTitle
    ? findLabel(rows, EGS_SELL_END, { fromRow: sellTitle.row + 2, col: SECTION_LABEL_COL })?.row
    : undefined;
  if (!sellTitle || sellEndRow === undefined) {
    forecastError(context, `There's no ${EGS_SELL_TABLE} table to tell each forecast's package`);
    return [];
  }
  const sellRowByRoom = new Map(
    range(sellTitle.row + 2, sellEndRow).flatMap((row) => {
      const room = textOf(cellAt(rows, row, SECTION_LABEL_COL));
      return room ? [[nameKey(room), row] as const] : [];
    }),
  );
  const columns = range(title.col, totalCol).map((col) => {
    const sellCol = sellTitle.col + (col - title.col);
    const heading = cellAt(rows, sellTitle.row + 1, sellCol);
    return {
      col,
      sellCol,
      nights: wholeNightsOf(cellAt(rows, headerRow, col)),
      label: numberOf(heading) === null ? textOf(heading) : null,
    };
  });

  return range(headerRow + 1, endRow).flatMap((row) => {
    const room = textOf(cellAt(rows, row, SECTION_LABEL_COL));
    if (!room) {
      return [];
    }
    const sellRow = sellRowByRoom.get(nameKey(room));
    const parsed = columns.map(({ col, sellCol, nights, label }) => {
      const value = cellAt(rows, row, col);
      const number = numberOf(value);
      if (!isUnpriced(value) && (number === null || number < 0)) {
        const which = nights ? `${nights}-night forecast` : `forecast in column ${columnLetter(col)}`;
        report(
          context,
          'forecastedPackages',
          'error',
          row,
          col,
          `The ${which} for "${room}" won't load: ${countProblem(value)}. Add it on the Forecasting tab.`,
        );
      }
      return {
        col,
        nights,
        label,
        count: number !== null && number > 0 ? Math.round(number) : 0,
        sell: sellRow === undefined ? null : priceOf(cellAt(rows, sellRow, sellCol)),
      };
    });
    return parsed.some(({ count }) => count > 0) ? [{ room, columns: parsed }] : [];
  });
}

// Values start in column C or D. The reach stops a blank value from picking up the label beside it.
function headerValue(rows: SheetRows, label: string) {
  const found = findLabel(rows, label, { toRow: HEADER_ROWS });
  if (!found) {
    return null;
  }
  for (let col = found.col + 1; col <= found.col + HEADER_VALUE_REACH; col++) {
    const value = cellAt(rows, found.row, col);
    if (!isBlank(value)) {
      return { value, row: found.row, col };
    }
  }
  return null;
}

function propertySetupIssue(context: SheetContext, message: string) {
  context.issues.push({ section: 'propertySetup', severity: 'warning', message });
}

function travelDates(context: SheetContext) {
  const fromCell = headerValue(context.rows, 'Travel From');
  const toCell = headerValue(context.rows, 'Travel To');
  if (!fromCell && !toCell) {
    return { travelFrom: null, travelTo: null };
  }
  const travelFrom = isoDateOf(fromCell?.value);
  const travelTo = isoDateOf(toCell?.value);
  if (!travelFrom || !travelTo) {
    propertySetupIssue(
      context,
      "Travel dates won't load: the workbook needs a date in both Travel From and Travel To.",
    );
    return { travelFrom: null, travelTo: null };
  }
  if (travelFrom > travelTo) {
    propertySetupIssue(
      context,
      `Travel dates won't load: Travel From (${formatDateShort(travelFrom)}) is after Travel To (${formatDateShort(travelTo)}).`,
    );
    return { travelFrom: null, travelTo: null };
  }
  return { travelFrom, travelTo };
}

export function parsePropertySetup(context: SheetContext): ParsedPropertySetup {
  const { rows } = context;
  const currency = textOf(headerValue(rows, 'Currency')?.value)?.toUpperCase() ?? null;
  const fxCell = headerValue(rows, 'FX Rate');
  const fxRate = positiveNumberOf(fxCell?.value);
  if (fxCell && fxRate === null) {
    propertySetupIssue(context, "The workbook's FX rate won't load: it isn't a number above 0.");
  }
  return {
    country: textOf(headerValue(rows, 'Destination Country')?.value),
    ...travelDates(context),
    currency,
    fxRate,
    fxNote: fxCell ? textOf(cellAt(rows, fxCell.row, fxCell.col + 1)) : null,
  };
}

// Merged cells put Basis two cells right of the label, and From, To and Comms two apart on the rows below.
export function parseOverrides(context: SheetContext): ParsedOverrides | 'off' | null {
  const { rows } = context;
  const heading = findLabel(rows, 'Overrides', { lastCol: OVERRIDES_LAST_COL });
  if (!heading) {
    missingSection(context, 'overrides', 'There is no Overrides section');
    return null;
  }
  const labelRow = heading.row + 1;
  const columns = [heading.col, heading.col + 2, heading.col + 4];
  const labelled = ['From', 'To', 'Comms'].every((label, index) =>
    findLabel(rows, label, { fromRow: labelRow, toRow: labelRow, col: columns[index] }),
  );
  if (!labelled) {
    missingSection(context, 'overrides', 'The Overrides section has no From, To and Comms labels');
    return null;
  }

  const basisText = textOf(cellAt(rows, heading.row, heading.col + 2));
  if (!basisText || basisText.toLowerCase() === OVERRIDES_OFF) {
    return 'off';
  }
  const basis = OVERRIDE_BASES[basisText.toLowerCase()];
  if (!basis) {
    context.issues.push({
      section: 'overrides',
      severity: 'error',
      message: `Overrides won't load: the Basis "${basisText}" isn't one the tool knows. Pick Hotel Revenue (Local) or Room Nights in the workbook and upload again.`,
    });
    return null;
  }

  const tiers: Array<ParsedOverrideTier> = [];
  let rejected = false;
  for (let row = labelRow + 1; row < rows.length; row++) {
    const [from, to, comms] = columns.map((col) => cellAt(rows, row, col));
    if (from === null && to === null && comms === null) {
      break;
    }
    const fromNumber = numberOf(from);
    const toNumber = numberOf(to);
    const fraction = numberOf(comms);
    if (toNumber === null || fraction === null || (fromNumber === null && from !== null)) {
      context.issues.push({
        section: 'overrides',
        severity: 'error',
        cell: cellAddress(row, heading.col),
        row: row + 1,
        message: 'the tier needs a number in To and Comms, and From must be a number or blank.',
      });
      rejected = true;
      continue;
    }
    tiers.push({ row: row + 1, from: fromNumber, to: toNumber, comms: round2(fraction * 100) });
  }
  // Each tier starts where the one before it ends, so dropping one would hand its range to the next.
  if (rejected) {
    context.issues.push({
      section: 'overrides',
      severity: 'error',
      message:
        "Overrides won't load: a tier can't be read, and skipping it would change the ranges of the tiers after it. Fix the workbook and upload again.",
    });
    return null;
  }
  return { basis, tiers };
}
